<template>
  <div v-if="userIsRegistered" class="w-full">
    <MenuBar :wall="undefined" :is-attached-to-wall="false" />
    <div id="dashboard-wrapper" class="flex w-full overflow-y-hidden">
      <DashboardSidebar ref="sidebar" class="fadein" />
      <Dashboard id="dashboard" class="fadein" :isnew-wall="isnewWall" :course-id="courseId" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeMount, onMounted } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'

  import { WallSelectionType } from '@/constants'

  import useWallStore from '@/composables/wallStore'
  import { useUserStore } from '@/composables/userStore'

  import useParcelService from '@/composables/parcel'
  import useFilterStore from '@/composables/filter'
  import useDialogStore from '@/composables/dialog'

  import MenuBar from '@/components/common/MenuBar.vue'
  import DashboardSidebar from '@/components/dashboard/DashboardSidebar.vue'
  import Dashboard from '@/components/dashboard/DashboardMain.vue'
  import LoadingScreen from '@/components/common/LoadingScreen.vue'
  import { DialogMessageType } from '@/common/classes'

  const filter = useFilterStore()
  const dialog = useDialogStore()

  const wallStore = useWallStore()
  const userStore = useUserStore()
  const parcelStore = useParcelService()

  const router = useRouter()
  const route = useRoute()

  const { t } = useI18n()

  const props = defineProps({
    wallsearchkey: {
      type: String,
      default: '',
    },
    wallsearchvalue: {
      type: String,
      default: '',
    },
  })

  const isnewWall = computed(() => props.wallsearchkey == 'newWall')
  const courseId = computed(() => props.wallsearchvalue)

  const userIsRegistered = computed(() => {
    const user = userStore.getters.user.value
    return !user || !user.status ? false : user.status.isRegistered
  })

  onBeforeMount(() => {
    wallStore.actions.resetSelectedWall() // reset selected wall selection
    if (userStore.getters.user.value && !userIsRegistered.value && !router.options.history.state.back?.toString().includes('login')) router.back() // do not allow anonymous users to get to the dashboard
  })

  onMounted(async () => {
    const { authenticated } = await userStore.actions.isValidSession()
    if (!authenticated) router.push('/login')
    else {
      // only proceed if user has a valid session
      await userStore.actions.getUserDetails()
      const mqttIsConnected = await connectToMQTT()
      if (mqttIsConnected) {
        if (userIsRegistered.value) {
          // remains on dashboard
          const res = await wallStore.actions.getUserWalls('')
          if (!res.success || res.error) router.push('/login')
          else {
            wallStore.actions.getTemplateWalls() // get templates (copy) to show in the ALL WALLS selection panel)
            filter.actions.setWallSelection(WallSelectionType.ALL)
          }
        } else if (!userIsRegistered.value) {
          router.go(-1)
        }
      } else dialog.actions.pushMessage('', DialogMessageType.Error, t('mqtterror'))
    }
  })

  function connectToMQTT(): Promise<boolean> {
    console.log('Connecting to MQTT..')
    return new Promise(async (resolve, reject) => {
      const user = userStore.getters.user.value
      if (user) {
        await parcelStore.actions.openMQTTConnection(user)
        await parcelStore.actions.unSubscribeTopic(user._id) // unsubscribe and re-subscribe (to prevent granted from being empty)
        await parcelStore.actions.subscribeTopic(user._id)
        for (const groupId of user.profile.groups.map((g) => g.originalId)) {
          await parcelStore.actions.unSubscribeTopic(groupId)
          await parcelStore.actions.subscribeTopic(groupId)
        }
        console.log('Connected to MQTT')
        resolve(true)
      } else reject(false)
    })
  }
</script>

<style scoped>
  #dashboard-wrapper {
    height: calc(100vh - 3.5rem);
  }

  #dashboard {
    min-width: 85%;
  }
</style>
